import type { Button, InputFrame } from "./input";
import { BUTTONS } from "./input";
import { surfaceAt, type Level } from "./level";
import { Skater, type SkaterEvent } from "./skater";
import { SPECIAL_FILL_POINTS } from "./special";
import { HILL_SITE } from "./site";
import { DEMO_STEP, type DemoLine } from "./demo-lines/script";
import { DEPOT_GRAFFITI, depotLines, DEPOT_PROPS, DEPOT_TARGETS } from "./demo-lines/depot";
import { parkLines } from "./demo-lines/park";
import { parkV2Lines } from "./demo-lines/park-v2";
import {
  STREET_GRAFFITI,
  streetLines,
  STREET_TARGETS,
} from "./demo-lines/street";
import {
  THEME_PARK_GRAFFITI,
  themeParkLines,
  THEME_PARK_PROPS,
  THEME_PARK_TARGETS,
} from "./demo-lines/theme-park";

// Demo mode: scripted lines played through the real sim. A line is a timed
// input script (stick + held buttons per segment), stepped at the sim's fixed
// rate, so the same line lands the same tricks in a test and on screen. The
// only thing the player works out for itself is balance: while grinding, on
// a lip trick or manualing, any segment that doesn't set the stick axis holds the needle
// steady, the way a player would. Every line has a test in `demo.test.ts`
// asserting it lands what it's meant to, so lines can't quietly rot as the
// physics changes.
// Each map's lines are in `demo-lines/<map>.ts`, written with the helpers in
// `demo-lines/script.ts`.

export {
  DEMO_STEP,
  type DemoLine,
  type DemoSegment,
} from "./demo-lines/script";

/** Every map's lines, in play order. None on the hill site (#219): they're all in the parks. */
export const DEMO_LINES: readonly DemoLine[] = HILL_SITE
  ? []
  : [
      ...parkLines(),
      ...streetLines(),
      ...themeParkLines(),
      ...parkV2Lines(),
      ...depotLines(),
      // Every map's "Hit 5 targets" challenge (#137), facing back the way they came.
      STREET_TARGETS,
      THEME_PARK_TARGETS,
      DEPOT_TARGETS,
      // ...and its "Wash off 3 graffiti tags" challenge (#151).
      STREET_GRAFFITI,
      THEME_PARK_GRAFFITI,
      DEPOT_GRAFFITI,
      // The washer knocking a cone and a can about (#151).
      THEME_PARK_PROPS,
      DEPOT_PROPS,
    ];

export function linesFor(mapId: string): DemoLine[] {
  return DEMO_LINES.filter((l) => l.mapId === mapId);
}

/** Small deterministic RNG (mulberry32), so a line's balance wobble is the same every run. */
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A fresh skater placed at the line's start, with a fixed RNG. */
export function demoSkater(level: Level, line: DemoLine): Skater {
  const sk = new Skater(level, seededRng(0x5eed));
  sk.x = line.start.x;
  sk.z = line.start.z;
  sk.y = surfaceAt(level, sk.x, sk.z).h;
  sk.yaw = line.start.yaw;
  sk.speed = line.start.speed;
  if (line.special) sk.special.add(SPECIAL_FILL_POINTS);
  return sk;
}

/** Stick value that steadies a balance needle (see `Skater.stepBalance`). */
function steady(b: { balance: number; balanceVel: number }): number {
  return Math.max(-1, Math.min(1, -(b.balance * 3 + b.balanceVel * 1.2)));
}

const NO_BUTTONS = Object.fromEntries(BUTTONS.map((b) => [b, false])) as Record<
  Button,
  boolean
>;

/** Plays one line: `next(skater)` gives the input for the next sim step, null once it's over. */
export class DemoPlayer {
  private stepIndex = 0;
  private prevHeld = NO_BUTTONS;
  /** Segment start times in steps. */
  private starts: number[] = [];
  private total: number;

  constructor(readonly line: DemoLine) {
    let t = 0;
    for (const seg of line.segments) {
      this.starts.push(t);
      t += Math.max(1, Math.round(seg.dur / DEMO_STEP));
    }
    this.total = t;
  }

  get done(): boolean {
    return this.stepIndex >= this.total;
  }

  next(sk: Skater): InputFrame | null {
    if (this.done) return null;
    let i = this.starts.length - 1;
    while (i > 0 && this.starts[i]! > this.stepIndex) i--;
    const seg = this.line.segments[i]!;
    this.stepIndex++;

    const held = { ...NO_BUTTONS };
    for (const b of seg.hold ?? []) held[b] = true;
    const pressed = { ...NO_BUTTONS };
    const released = { ...NO_BUTTONS };
    for (const b of BUTTONS) {
      pressed[b] = held[b] && !this.prevHeld[b];
      released[b] = !held[b] && this.prevHeld[b];
    }
    this.prevHeld = held;

    const moveX =
      seg.x ??
      (sk.grind
        ? steady(sk.grind)
        : sk.lip
          ? steady(sk.lip)
          : sk.skitch
            ? steady(sk.skitch)
            : 0);
    const moveY = seg.y ?? (sk.manual ? steady(sk.manual) : 0);
    return { moveX, moveY, held, pressed, released, source: "keyboard" };
  }
}

/**
 * Runs a whole line headless: what the tests check, and a handy tuning tool.
 * `onStep` sees the skater and that step's events after every sim step.
 */
export function playLine(
  level: Level,
  line: DemoLine,
  tail = 1.5,
  onStep?: (sk: Skater, events: readonly SkaterEvent[]) => void,
): { skater: Skater; events: SkaterEvent[] } {
  const sk = demoSkater(level, line);
  const player = new DemoPlayer(line);
  const events: SkaterEvent[] = [];
  const step = (f: InputFrame) => {
    const out = sk.step(f, DEMO_STEP);
    events.push(...out);
    onStep?.(sk, out);
  };
  for (let f = player.next(sk); f; f = player.next(sk)) step(f);
  // Let the last trick land.
  const idle = player.next(sk) ?? idleFrame();
  for (let t = 0; t < tail; t += DEMO_STEP) step(idle);
  return { skater: sk, events };
}

function idleFrame(): InputFrame {
  return {
    moveX: 0,
    moveY: 0,
    held: { ...NO_BUTTONS },
    pressed: { ...NO_BUTTONS },
    released: { ...NO_BUTTONS },
    source: "keyboard",
  };
}
